import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  GraduationCap, Upload, Video, ArrowRight, ArrowLeft, Loader2, CheckCircle2,
  Camera, StopCircle, RotateCcw, ShieldCheck, X, Clock,
} from "lucide-react";

const MAX_UPLOAD_MB = 25;

/**
 * StudentApply — public marketing entry point that funnels enrolled students
 * through a short verification flow (ID upload → live video → submit) to
 * unlock Full Access as a comp.
 *   1. Anon visitors are shown the pitch + a Sign in / Create account CTA that
 *      brings them back here.
 *   2. Signed-in visitors submit a 3-step form: school details → photo of
 *      student ID → 15-second selfie video → review + submit.
 *   3. If they already have an application, we short-circuit into a status
 *      panel showing the current state.
 */
export default function StudentApply() {
  const { user, status } = useAuth();
  const nav = useNavigate();

  const [step, setStep] = useState(1);
  const [checking, setChecking] = useState(true);
  const [existing, setExisting] = useState(null);

  const [school, setSchool] = useState("");
  const [program, setProgram] = useState("");
  const [gradYear, setGradYear] = useState("");
  const [idUpload, setIdUpload] = useState(null);         // { url, kind }
  const [videoUpload, setVideoUpload] = useState(null);   // { url, kind }
  const [submitting, setSubmitting] = useState(false);
  const [website, setWebsite] = useState("");             // honeypot

  useEffect(() => {
    if (status !== "authenticated") { setChecking(false); return; }
    let alive = true;
    api.get("/students/me")
      .then((r) => { if (alive) setExisting(r.data?.application || null); })
      .catch(() => {})
      .finally(() => { if (alive) setChecking(false); });
    return () => { alive = false; };
  }, [status]);

  const submit = async () => {
    if (!school.trim()) return toast.error("Please add your school name");
    if (!idUpload)     return toast.error("Please upload your student ID");
    if (!videoUpload)  return toast.error("Please record a short verification video");
    setSubmitting(true);
    try {
      const payload = {
        school: school.trim(),
        program: program.trim(),
        graduation_year: gradYear ? parseInt(gradYear, 10) : null,
        id_upload_url: idUpload.url,
        video_upload_url: videoUpload.url,
        website,
      };
      await api.post("/students/apply", payload);
      toast.success("Application submitted — we'll review within 48 hours");
      const { data } = await api.get("/students/me");
      setExisting(data?.application || null);
    } catch (err) {
      toast.error(formatError(err));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="student-apply-page">
      <header className="border-b border-shell-border/70 bg-white/80 backdrop-blur">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-6 md:px-10 py-4">
          <Link to="/" className="font-display text-lg tracking-tight hover:text-primary transition">CPG Institute</Link>
          <Link to="/account/subscription" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground transition">
            <ArrowLeft className="w-3.5 h-3.5" /> Pricing
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <p className="text-xs uppercase tracking-[0.28em] text-primary font-bold inline-flex items-center gap-1.5">
          <GraduationCap className="w-3 h-3" /> Students — Free
        </p>
        <h1 className="font-display text-4xl md:text-6xl mt-3 tracking-tight leading-[1.05]">
          Full Access, on the house — <span className="text-grad">while you're enrolled.</span>
        </h1>
        <p className="mt-5 text-muted-foreground leading-relaxed max-w-xl">
          Currently enrolled at an accredited university, community college, or CPG-focused bootcamp? Verify with your student ID and a short video and we'll unlock the entire platform for <strong>365 days</strong>.
        </p>

        {checking && (
          <div className="mt-8 inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking your application…
          </div>
        )}

        {/* Not signed in */}
        {!checking && status !== "authenticated" && (
          <div className="mt-10 rounded-2xl border border-shell-border bg-white p-6" data-testid="student-apply-signin-gate">
            <p className="text-sm text-muted-foreground max-w-lg">
              You'll need an account before you apply — that's where we'll grant access if you're approved. Takes 30 seconds.
            </p>
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <Link to="/signup?next=/apply/student" data-testid="student-apply-signup" className="inline-flex items-center gap-1.5 rounded-full bg-primary text-white px-5 py-2.5 text-sm font-bold hover:bg-primary/90 transition">
                Create account <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login?next=/apply/student" data-testid="student-apply-signin" className="inline-flex items-center gap-1.5 rounded-full border border-shell-border bg-white px-5 py-2.5 text-sm font-semibold hover:border-primary/40 transition">
                I already have an account
              </Link>
            </div>
          </div>
        )}

        {/* Existing application status */}
        {!checking && status === "authenticated" && existing && (
          <ExistingApplicationStatus application={existing} onDismiss={() => nav("/dashboard")} />
        )}

        {/* Fresh apply form */}
        {!checking && status === "authenticated" && !existing && (
          <div className="mt-10">
            <StepProgress step={step} />
            {step === 1 && (
              <StepSchool
                school={school} setSchool={setSchool}
                program={program} setProgram={setProgram}
                gradYear={gradYear} setGradYear={setGradYear}
                onNext={() => setStep(2)}
              />
            )}
            {step === 2 && (
              <StepIdUpload
                current={idUpload} setUpload={setIdUpload}
                onBack={() => setStep(1)} onNext={() => setStep(3)}
              />
            )}
            {step === 3 && (
              <StepVideoUpload
                current={videoUpload} setUpload={setVideoUpload}
                userName={user?.name || "your name"}
                school={school}
                onBack={() => setStep(2)} onNext={() => setStep(4)}
              />
            )}
            {step === 4 && (
              <StepReview
                school={school} program={program} gradYear={gradYear}
                idUpload={idUpload} videoUpload={videoUpload}
                submitting={submitting}
                website={website} setWebsite={setWebsite}
                onBack={() => setStep(3)} onSubmit={submit}
              />
            )}
          </div>
        )}
      </main>
    </div>
  );
}


function StepProgress({ step }) {
  const steps = ["School", "Student ID", "Video", "Review"];
  return (
    <ol className="flex items-center gap-2 mb-8 text-[11px] font-semibold uppercase tracking-[0.14em]" data-testid="student-apply-progress">
      {steps.map((label, i) => {
        const idx = i + 1;
        const active = step === idx;
        const done = step > idx;
        return (
          <React.Fragment key={label}>
            <li className={[
              "flex items-center gap-1.5 rounded-full px-3 py-1.5 border transition",
              active ? "bg-primary text-white border-primary" :
              done ? "bg-emerald-500/15 text-emerald-800 border-emerald-500/30" :
              "bg-white text-muted-foreground border-shell-border",
            ].join(" ")}>
              <span className="w-4 h-4 rounded-full grid place-items-center text-[10px] font-bold bg-white/20">
                {done ? "✓" : idx}
              </span>
              {label}
            </li>
            {i < steps.length - 1 && <span className="text-muted-foreground/60">—</span>}
          </React.Fragment>
        );
      })}
    </ol>
  );
}


function StepSchool({ school, setSchool, program, setProgram, gradYear, setGradYear, onNext }) {
  return (
    <div className="rounded-2xl border border-shell-border bg-white p-6" data-testid="student-apply-step-school">
      <h2 className="font-display text-2xl">Tell us about your program</h2>
      <p className="mt-1 text-sm text-muted-foreground">Just the essentials — we'll cross-check with your ID.</p>
      <div className="mt-6 space-y-4">
        <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          School / institution *
          <input
            type="text" value={school} onChange={(e) => setSchool(e.target.value)}
            data-testid="student-apply-school"
            placeholder="e.g. Michigan State University"
            className="mt-1 w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/40"
          />
        </label>
        <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          Program (optional)
          <input
            type="text" value={program} onChange={(e) => setProgram(e.target.value)}
            data-testid="student-apply-program"
            placeholder="Food Science, Business, Culinary Arts…"
            className="mt-1 w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/40"
          />
        </label>
        <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground max-w-xs">
          Expected graduation year (optional)
          <input
            type="number" min="1900" max="2100"
            value={gradYear} onChange={(e) => setGradYear(e.target.value)}
            data-testid="student-apply-year"
            placeholder="2027"
            className="mt-1 w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/40"
          />
        </label>
      </div>
      <div className="mt-8">
        <button type="button" onClick={onNext} disabled={!school.trim()}
          data-testid="student-apply-next-1"
          className="inline-flex items-center gap-1.5 rounded-full bg-primary text-white px-5 py-2.5 text-sm font-bold hover:bg-primary/90 disabled:opacity-60 transition">
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}


function StepIdUpload({ current, setUpload, onBack, onNext }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose a PNG or JPG image");
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) return toast.error(`Image must be under ${MAX_UPLOAD_MB} MB`);
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/students/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUpload(data);
      toast.success("ID uploaded");
    } catch (err) {
      toast.error(formatError(err));
    } finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div className="rounded-2xl border border-shell-border bg-white p-6" data-testid="student-apply-step-id">
      <h2 className="font-display text-2xl">Upload your student ID</h2>
      <p className="mt-1 text-sm text-muted-foreground max-w-lg">Clear photo of the front side is fine — we just need to see your name, school, and expiration date. Redact anything else you don't want us to see.</p>

      {current ? (
        <div className="mt-5 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-center gap-3" data-testid="student-apply-id-uploaded">
          <img src={current.url} alt="Student ID" className="w-24 h-16 object-cover rounded-lg border border-shell-border" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-emerald-800 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Uploaded</p>
            <p className="text-[11px] text-muted-foreground truncate">{current.url}</p>
          </div>
          <button type="button" onClick={() => setUpload(null)} data-testid="student-apply-id-clear" className="rounded-full p-1.5 hover:bg-shell-border/40 transition">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button" onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="student-apply-id-pick"
          className="mt-5 w-full rounded-2xl border-2 border-dashed border-shell-border bg-white p-8 text-center hover:border-primary/40 transition disabled:opacity-60"
        >
          {uploading ? (
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-primary" />
          ) : (
            <Upload className="w-8 h-8 mx-auto text-primary" />
          )}
          <p className="mt-3 font-semibold text-sm">{uploading ? "Uploading…" : "Choose a photo or drag it in"}</p>
          <p className="text-[11px] text-muted-foreground mt-1">PNG or JPG · up to {MAX_UPLOAD_MB} MB</p>
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="student-apply-id-file" />

      <div className="mt-8 flex items-center gap-3">
        <button type="button" onClick={onBack} data-testid="student-apply-back-2" className="inline-flex items-center gap-1.5 rounded-full border border-shell-border bg-white px-4 py-2 text-xs font-semibold hover:border-primary/40 transition">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <button type="button" onClick={onNext} disabled={!current}
          data-testid="student-apply-next-2"
          className="inline-flex items-center gap-1.5 rounded-full bg-primary text-white px-5 py-2.5 text-sm font-bold hover:bg-primary/90 disabled:opacity-60 transition">
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}


function StepVideoUpload({ current, setUpload, userName, school, onBack, onNext }) {
  const videoRef = useRef(null);
  const chunksRef = useRef([]);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => () => stopStream(), []);

  const stopStream = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch {}
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        videoRef.current.play();
      }
      const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
        ? "video/webm;codecs=vp9,opus"
        : "video/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (ev) => { if (ev.data?.size) chunksRef.current.push(ev.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        setPreviewUrl(URL.createObjectURL(blob));
        stopStream();
        uploadBlob(blob);
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => {
        if (s + 1 >= 20) { stopRecording(); return 20; }
        return s + 1;
      }), 1000);
    } catch (err) {
      toast.error("Couldn't access your camera. Please allow camera + mic permissions.");
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state === "recording") {
      recorderRef.current.stop();
    }
    setRecording(false);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const uploadBlob = async (blob) => {
    setUploading(true);
    try {
      const file = new File([blob], `verification-${Date.now()}.webm`, { type: "video/webm" });
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/students/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUpload(data);
      toast.success("Verification video uploaded");
    } catch (err) {
      toast.error(formatError(err));
    } finally { setUploading(false); }
  };

  const resetRecording = () => {
    setPreviewUrl(null);
    setUpload(null);
    setSeconds(0);
  };

  return (
    <div className="rounded-2xl border border-shell-border bg-white p-6" data-testid="student-apply-step-video">
      <h2 className="font-display text-2xl">Record a short verification video</h2>
      <p className="mt-1 text-sm text-muted-foreground max-w-lg">Look at the camera and say: <em>"Hi, I'm {userName}. I'm a student at {school || "your school"} and this is my application to CPG Institute."</em> That's it. Up to 20 seconds.</p>

      <div className="mt-6 rounded-2xl bg-black/95 aspect-video overflow-hidden relative">
        {current ? (
          <video src={previewUrl || current.url} controls className="w-full h-full object-contain" data-testid="student-apply-video-preview" />
        ) : (
          <video ref={videoRef} className="w-full h-full object-cover" data-testid="student-apply-video-live" />
        )}
        {recording && (
          <div className="absolute top-3 left-3 inline-flex items-center gap-2 rounded-full bg-rose-500 text-white px-3 py-1 text-[11px] font-bold">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> REC {seconds}s
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/80 grid place-items-center text-white text-sm font-semibold">
            <div className="inline-flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Uploading video…</div>
          </div>
        )}
      </div>

      <div className="mt-5 flex items-center gap-2 flex-wrap">
        {!current && !recording && (
          <button type="button" onClick={startRecording} data-testid="student-apply-video-start" className="inline-flex items-center gap-1.5 rounded-full bg-rose-500 text-white px-5 py-2 text-xs font-bold hover:bg-rose-600 transition">
            <Camera className="w-3.5 h-3.5" /> Start recording
          </button>
        )}
        {recording && (
          <button type="button" onClick={stopRecording} data-testid="student-apply-video-stop" className="inline-flex items-center gap-1.5 rounded-full bg-shell-side text-white px-5 py-2 text-xs font-bold hover:bg-shell-side/90 transition">
            <StopCircle className="w-3.5 h-3.5" /> Stop
          </button>
        )}
        {current && (
          <button type="button" onClick={resetRecording} data-testid="student-apply-video-reset" className="inline-flex items-center gap-1.5 rounded-full border border-shell-border bg-white px-4 py-2 text-xs font-semibold hover:border-primary/40 transition">
            <RotateCcw className="w-3.5 h-3.5" /> Re-record
          </button>
        )}
      </div>

      <div className="mt-8 flex items-center gap-3">
        <button type="button" onClick={onBack} data-testid="student-apply-back-3" className="inline-flex items-center gap-1.5 rounded-full border border-shell-border bg-white px-4 py-2 text-xs font-semibold hover:border-primary/40 transition">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <button type="button" onClick={onNext} disabled={!current}
          data-testid="student-apply-next-3"
          className="inline-flex items-center gap-1.5 rounded-full bg-primary text-white px-5 py-2.5 text-sm font-bold hover:bg-primary/90 disabled:opacity-60 transition">
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}


function StepReview({ school, program, gradYear, idUpload, videoUpload, submitting, website, setWebsite, onBack, onSubmit }) {
  return (
    <div className="rounded-2xl border border-shell-border bg-white p-6" data-testid="student-apply-step-review">
      <h2 className="font-display text-2xl">Review + submit</h2>
      <p className="mt-1 text-sm text-muted-foreground">One last look before we send this to the review queue.</p>

      <dl className="mt-6 divide-y divide-shell-border rounded-2xl border border-shell-border overflow-hidden">
        <div className="grid grid-cols-3 gap-2 px-4 py-3">
          <dt className="text-[11px] uppercase tracking-[0.12em] font-bold text-muted-foreground col-span-1">School</dt>
          <dd className="col-span-2 text-sm">{school}</dd>
        </div>
        {program && (
          <div className="grid grid-cols-3 gap-2 px-4 py-3">
            <dt className="text-[11px] uppercase tracking-[0.12em] font-bold text-muted-foreground col-span-1">Program</dt>
            <dd className="col-span-2 text-sm">{program}</dd>
          </div>
        )}
        {gradYear && (
          <div className="grid grid-cols-3 gap-2 px-4 py-3">
            <dt className="text-[11px] uppercase tracking-[0.12em] font-bold text-muted-foreground col-span-1">Graduating</dt>
            <dd className="col-span-2 text-sm">{gradYear}</dd>
          </div>
        )}
        <div className="grid grid-cols-3 gap-2 px-4 py-3">
          <dt className="text-[11px] uppercase tracking-[0.12em] font-bold text-muted-foreground col-span-1">Student ID</dt>
          <dd className="col-span-2">
            {idUpload && <img src={idUpload.url} alt="ID" className="w-32 h-20 object-cover rounded border border-shell-border" />}
          </dd>
        </div>
        <div className="grid grid-cols-3 gap-2 px-4 py-3">
          <dt className="text-[11px] uppercase tracking-[0.12em] font-bold text-muted-foreground col-span-1">Video</dt>
          <dd className="col-span-2">
            {videoUpload && <video src={videoUpload.url} controls className="w-64 rounded border border-shell-border" />}
          </dd>
        </div>
      </dl>

      <div className="mt-4 rounded-xl bg-shell-border/20 p-4 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          We review new student applications within <strong>48 hours</strong>. Approved accounts unlock Full Access for 365 days. Your uploads are used only to verify enrolment and are deleted 90 days after approval or rejection.
        </p>
      </div>

      {/* Honeypot — hidden from real users */}
      <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} className="absolute -left-[9999px] w-0 h-0" aria-hidden tabIndex={-1} autoComplete="off" />

      <div className="mt-8 flex items-center gap-3">
        <button type="button" onClick={onBack} data-testid="student-apply-back-4" className="inline-flex items-center gap-1.5 rounded-full border border-shell-border bg-white px-4 py-2 text-xs font-semibold hover:border-primary/40 transition">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <button type="button" onClick={onSubmit} disabled={submitting}
          data-testid="student-apply-submit"
          className="inline-flex items-center gap-1.5 rounded-full bg-primary text-white px-6 py-3 text-sm font-bold hover:bg-primary/90 disabled:opacity-60 transition">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          {submitting ? "Submitting…" : "Submit application"}
        </button>
      </div>
    </div>
  );
}


function ExistingApplicationStatus({ application, onDismiss }) {
  const map = {
    pending:  { label: "Under review", tone: "bg-pink-500/15 text-pink-800 border-pink-500/30", icon: Clock },
    approved: { label: "Approved", tone: "bg-emerald-500/15 text-emerald-800 border-emerald-500/30", icon: CheckCircle2 },
    rejected: { label: "Declined", tone: "bg-rose-500/15 text-rose-800 border-rose-500/30", icon: X },
  };
  const meta = map[application.status] || map.pending;
  const Icon = meta.icon;

  return (
    <div className="mt-10 rounded-2xl border border-shell-border bg-white p-6" data-testid="student-apply-existing">
      <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] ${meta.tone}`}>
        <Icon className="w-3.5 h-3.5" /> {meta.label}
      </span>
      <h2 className="mt-4 font-display text-2xl">Your student application</h2>
      <dl className="mt-4 space-y-2 text-sm">
        <div><dt className="inline text-muted-foreground">School: </dt><dd className="inline font-semibold">{application.school}</dd></div>
        {application.program && <div><dt className="inline text-muted-foreground">Program: </dt><dd className="inline font-semibold">{application.program}</dd></div>}
        <div><dt className="inline text-muted-foreground">Submitted: </dt><dd className="inline font-semibold">{new Date(application.submitted_at).toLocaleDateString()}</dd></div>
        {application.reviewed_at && <div><dt className="inline text-muted-foreground">Reviewed: </dt><dd className="inline font-semibold">{new Date(application.reviewed_at).toLocaleDateString()}</dd></div>}
        {application.comp_expires_at && <div><dt className="inline text-muted-foreground">Access until: </dt><dd className="inline font-semibold">{new Date(application.comp_expires_at).toLocaleDateString()}</dd></div>}
        {application.rejection_reason && <div><dt className="inline text-muted-foreground">Note: </dt><dd className="inline italic">{application.rejection_reason}</dd></div>}
      </dl>
      <button type="button" onClick={onDismiss} className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-shell-side text-white px-5 py-2.5 text-sm font-semibold hover:bg-shell-side/90 transition">
        Back to dashboard <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}
